import React, { useState } from 'react';
import ReportFilterBar from '../components/ReportFilterBar';
import ReportTable from '../components/ReportTable';
import ReportDownloadButtons from '../components/ReportDownloadButtons';
import { API_BASE_URL } from '../services/api';

const MarkToMarketReportPage = () => {
  const [filters, setFilters] = useState({});
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [outstanding, setOutstanding] = useState({ sells: [], buybacks: [], tbillSells: [] });
  const [totals, setTotals] = useState(null);
  const view = filters.view || 'prices';
  const isPortfolioView = view === 'portfolio';

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleGenerateReport = async (filtersArg) => {
      const usedFilters = filtersArg || filters;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      Object.entries(usedFilters || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          params.set(key, value);
        }
      });
      params.set('page', '1');
      params.set('pageSize', String(pagination.pageSize));
      const res = await fetch(`${API_BASE_URL}/reports/mark-to-market?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch report');
      const json = await res.json();
      setResults(json.data || []);
      setPagination(p => ({ ...p, page: 1, total: json.total || 0 }));
      setOutstanding(json.outstanding || { sells: [], buybacks: [], tbillSells: [] });
      setTotals(json.totals || null);
    } catch (e) {
      setError(e.message || 'Error fetching report');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (newPagination) => {
    setPagination(newPagination);
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          params.set(key, value);
        }
      });
      params.set('page', String(newPagination.page));
      params.set('pageSize', String(newPagination.pageSize));
      const res = await fetch(`${API_BASE_URL}/reports/mark-to-market?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch report');
      const json = await res.json();
      setResults(json.data || []);
      setPagination(p => ({ ...newPagination, total: json.total || 0 }));
    } catch (e) {
      setError(e.message || 'Error fetching report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          params.set(key, value);
        }
      });
      params.set('format', format);
      const res = await fetch(`${API_BASE_URL}/reports/mark-to-market?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to download report');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const baseName = (filters.view || 'prices') === 'portfolio' ? 'portfolio_mark_to_market' : 'mark_to_market_report';
      a.download = `${baseName}.${format === 'excel' ? 'xlsx' : format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Error downloading report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ui-page">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Mark to Market Report</h2>
      <ReportFilterBar
        filters={filters}
        onChange={handleFilterChange}
        onGenerate={handleGenerateReport}
        reportType="mark-to-market"
        showReportView
      />
      <ReportDownloadButtons onDownload={handleDownload} disabled={loading || results.length === 0} />
      <ReportTable
        data={results}
        pagination={pagination}
        loading={loading}
        error={error}
        onPageChange={handlePageChange}
        reportType={isPortfolioView ? 'mark-to-market-portfolio' : 'mark-to-market'}
      />

      {isPortfolioView && totals && (
        <div className="mt-3 bg-white border border-gray-200 rounded-lg shadow-soft p-3 flex flex-wrap gap-6 text-sm">
          <span className="font-semibold text-gray-700">GSEC Portfolio</span>
          <span className="text-gray-600">Holdings: {Number(totals.holdings || 0).toLocaleString()}</span>
          <span className="text-gray-600">
            Face Value Rs.: {Number(totals.face_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          <span className={Number(totals.profit_loss || 0) < 0 ? 'text-red-600' : 'text-green-700'}>
            Profit (Loss) Rs.: {Number(totals.profit_loss || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          <span className="text-gray-400">Open Sell/Buy buyback positions are excluded.</span>
        </div>
      )}

      {(outstanding.sells.length > 0 || outstanding.buybacks.length > 0 || (outstanding.tbillSells || []).length > 0) && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Outstanding Sell / Buyback (not yet settled as of selected date)
          </h3>
          <p className="text-sm text-gray-500 mb-3">
            Exposure that will change the MTM position once approved/settled, but isn't reflected in the holdings above yet.
          </p>

          {outstanding.sells.length > 0 && (
            <div className="mb-4 bg-white border border-gray-200 rounded-lg shadow-soft p-3">
              <h4 className="font-medium text-gray-700 mb-2">Pending GSec Sells</h4>
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="text-left px-3 py-2 bg-gray-50 border-b">Deal Number</th>
                    <th className="text-left px-3 py-2 bg-gray-50 border-b">ISIN</th>
                    <th className="text-right px-3 py-2 bg-gray-50 border-b">Face Value</th>
                    <th className="text-left px-3 py-2 bg-gray-50 border-b">Value Date</th>
                    <th className="text-left px-3 py-2 bg-gray-50 border-b">Approval Level</th>
                  </tr>
                </thead>
                <tbody>
                  {outstanding.sells.map((s) => (
                    <tr key={s.deal_number}>
                      <td className="px-3 py-2 border-b">{s.deal_number}</td>
                      <td className="px-3 py-2 border-b">{s.isin}</td>
                      <td className="px-3 py-2 border-b text-right">{Number(s.face_value || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 border-b">{s.value_date ? String(s.value_date).split('T')[0] : ''}</td>
                      <td className="px-3 py-2 border-b">{s.current_approval_level}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {outstanding.buybacks.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-soft p-3">
              <h4 className="font-medium text-gray-700 mb-2">Pending Buyback Deals</h4>
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="text-left px-3 py-2 bg-gray-50 border-b">Deal Number</th>
                    <th className="text-left px-3 py-2 bg-gray-50 border-b">ISIN</th>
                    <th className="text-right px-3 py-2 bg-gray-50 border-b">Face Value</th>
                    <th className="text-left px-3 py-2 bg-gray-50 border-b">Value Date</th>
                    <th className="text-left px-3 py-2 bg-gray-50 border-b">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {outstanding.buybacks.map((b) => (
                    <tr key={b.deal_number}>
                      <td className="px-3 py-2 border-b">{b.deal_number}</td>
                      <td className="px-3 py-2 border-b">{b.isin}</td>
                      <td className="px-3 py-2 border-b text-right">{Number(b.face_value || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 border-b">{b.value_date ? String(b.value_date).split('T')[0] : ''}</td>
                      <td className="px-3 py-2 border-b">{b.deal_status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(outstanding.tbillSells || []).length > 0 && (
            <div className="mt-4 bg-white border border-gray-200 rounded-lg shadow-soft p-3">
              <h4 className="font-medium text-gray-700 mb-2">Pending T-Bill Sells</h4>
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="text-left px-3 py-2 bg-gray-50 border-b">Deal Number</th>
                    <th className="text-left px-3 py-2 bg-gray-50 border-b">ISIN</th>
                    <th className="text-right px-3 py-2 bg-gray-50 border-b">Face Value</th>
                    <th className="text-left px-3 py-2 bg-gray-50 border-b">Value Date</th>
                    <th className="text-left px-3 py-2 bg-gray-50 border-b">Approval Level</th>
                  </tr>
                </thead>
                <tbody>
                  {outstanding.tbillSells.map((s) => (
                    <tr key={s.deal_number}>
                      <td className="px-3 py-2 border-b">{s.deal_number}</td>
                      <td className="px-3 py-2 border-b">{s.isin}</td>
                      <td className="px-3 py-2 border-b text-right">{Number(s.face_value || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 border-b">{s.value_date ? String(s.value_date).split('T')[0] : ''}</td>
                      <td className="px-3 py-2 border-b">{s.current_approval_level}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MarkToMarketReportPage;
